import React, { useState, useContext, useEffect } from 'react';
import { Table, Button, ButtonGroup } from 'react-bootstrap';
import { ProductContext } from '../../ProductProvider';
import { CSVLink } from "react-csv";
import ReactToPrint from "react-to-print";
import { downloadMarkSheetHeaders, courseDetailsList } from '../../ProData';
import logo from '../../assets/images/logo/COLLEGELOGO.jpeg';

const config = require('../../config.json');

export default function MarkSheetDisplay(props) {
    const { loggedUser, validateLogin, handleImageError, myProfileImage } = useContext(ProductContext);
    const { profileDetails, markDetails } = props;
    const [dispReady, setDispReady] = useState(false);
    const componentRef = React.useRef(null);
    const [creditsEval, setCreditsEval] = useState(0);
    const [totalPoints, setTotalPoints] = useState(0);
    const [totalCredits, setTotalCredits] = useState(0);
    const [compER, setCompER] = useState("-");
    const [finalGrade, setFinalGrade] = useState("-");
    const [finalQual, setFinalQual] = useState("-");
    const [avgMark, setAvgMark] = useState("-");
    const csvLinkEl = React.createRef();

    useEffect(() => {
        if (loggedUser.isAuthenticated) {
            processMarks(markDetails);
            setDispReady(true);
        } else {
            validateLogin();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loggedUser, markDetails]);

    const handleDownload = (event) => {
        event.preventDefault();
        if (csvLinkEl.current) {
            csvLinkEl.current.link.click();
        }
    };

    const reactToPrintContent = React.useCallback(() => {
        return componentRef.current;
    }, []);

    const reactToPrintTrigger = React.useCallback(() => {
        return (
            <Button variant="outline-primary" size="sm" className="d-inline-flex align-items-center gap-1">
                <i className="fa fa-print"></i> Print Official Transcript
            </Button>
        );
    }, []);

    const processMarks = (item) => {
        const tempCourseDetailsList = courseDetailsList;
        let tempTotalPoints = 0;
        let tempCreditEval = 0;
        let tempTotalCredits = 0;

        for (const i in item) {
            if (tempCourseDetailsList[i]) {
                tempCourseDetailsList[i].courseMarks = parseInt(item[i].courseMarks) || 0;
                tempCourseDetailsList[i].coursePoints = parseInt(item[i].coursePoints) || 0;
            }
            tempTotalPoints += (parseInt(item[i].coursePoints) || 0);
            tempCreditEval += (parseInt(item[i].courseCredit) || 0);
            tempTotalCredits += (parseInt(item[i].courseCredit) || 0);
        }

        const testAvgMark = tempCreditEval > 0
            ? (parseFloat(tempTotalPoints) / parseFloat(tempCreditEval)).toFixed(2)
            : "0.00";

        const tempComp = (markDetails || []).filter((it) => it.courseName === "COMPREHENSIVE EXAM MARKS");
        let tempCompEM = "-";
        let tempFinalQual = 0;

        if (tempComp.length > 0 && tempComp[0].courseMarks) {
            tempCompEM = tempComp[0].courseMarks;
            tempFinalQual = ((parseFloat(testAvgMark) + parseFloat(tempComp[0].courseMarks)) / 2).toFixed(2);
            setFinalQual(tempFinalQual);
        } else {
            tempCompEM = "-";
            tempFinalQual = testAvgMark;
            setFinalQual(tempFinalQual);
        }

        let tempGrade = "-";
        const qualNum = parseFloat(tempFinalQual);
        if (qualNum < 40) {
            tempGrade = "PASSED";
        } else if (qualNum >= 40 && qualNum < 50) {
            tempGrade = "THIRD CLASS";
        } else if (qualNum >= 50 && qualNum < 60) {
            tempGrade = "SECOND CLASS";
        } else if (qualNum >= 60 && qualNum < 75) {
            tempGrade = "FIRST CLASS";
        } else if (qualNum >= 75 && qualNum <= 100) {
            tempGrade = "DISTINCTION";
        }

        setCreditsEval(tempCreditEval || 106);
        setCompER(tempCompEM);
        setFinalGrade(tempGrade);
        setTotalCredits(tempTotalCredits);
        setTotalPoints(tempTotalPoints);
        setAvgMark(testAvgMark);
    };

    const studentAvatarUrl = profileDetails && profileDetails.avatar && profileDetails.applicationNo
        ? `${config.s3.profileUrl}${profileDetails.applicationNo}/${profileDetails.avatar}?t=${new Date().getTime()}`
        : myProfileImage || logo;

    return (
        <div className="marksheet-display-wrapper animate-fade-in-up">
            {/* Header Toolbar */}
            <div className="d-flex justify-content-between flex-wrap align-items-center py-3 my-2">
                <div>
                    <h5 className="fw-bold mb-1 text-primary">
                        <i className="fa fa-certificate me-2"></i> Official Statement of Marks
                    </h5>
                    <span className="text-muted small">Academic Year 2024-2025 • Faculty of Theology</span>
                </div>
                <div className="btn-toolbar mb-2 mb-md-0">
                    <ButtonGroup className="gap-2">
                        <ReactToPrint
                            content={reactToPrintContent}
                            documentTitle={`Marksheet_${profileDetails.applicantName || 'Student'}`}
                            trigger={reactToPrintTrigger}
                        />
                        <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={handleDownload}
                            className="d-inline-flex align-items-center gap-1"
                        >
                            <i className="fa fa-download"></i> CSV Export
                        </Button>
                        <CSVLink
                            headers={downloadMarkSheetHeaders}
                            filename="Marksheet_Records.csv"
                            data={markDetails || []}
                            ref={csvLinkEl}
                            style={{ display: 'none' }}
                        />
                    </ButtonGroup>
                </div>
            </div>

            {/* Official Certificate Parchment Container */}
            {dispReady && (
                <div
                    ref={componentRef}
                    className="marksheet-certificate"
                    style={{
                        background: '#ffffff',
                        border: '8px double #1e3a8a',
                        borderRadius: '16px',
                        padding: '36px 32px',
                        boxShadow: '0 15px 35px rgba(0,0,0,0.06)',
                        position: 'relative'
                    }}
                >
                    {/* Watermark Crest */}
                    <img
                        className="marksheet-watermark"
                        src={logo}
                        alt="Watermark"
                        style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: '380px',
                            opacity: 0.04,
                            pointerEvents: 'none',
                            zIndex: 0
                        }}
                    />

                    <div style={{ position: 'relative', zIndex: 1 }}>
                        {/* Institutional Header */}
                        <div className="text-center mb-4 pb-3 border-bottom border-2 border-primary">
                            <img
                                src={logo}
                                alt="College Logo"
                                width="84"
                                height="84"
                                className="rounded-circle shadow-sm mb-2"
                                style={{ border: '3px solid #1e3a8a' }}
                            />
                            <h3 className="fw-bold mb-1" style={{ color: '#1e3a8a', fontFamily: 'Outfit, sans-serif' }}>
                                VIDYAJYOTI COLLEGE OF THEOLOGY
                            </h3>
                            <h6 className="fw-semibold text-muted mb-1 text-uppercase tracking-wide" style={{ fontSize: '0.9rem', letterSpacing: '0.08em' }}>
                                Faculty of Theology • Institute of Religious Studies
                            </h6>
                            <p className="small text-muted mb-2">Raj Niwas Marg, Delhi - 110054, India</p>

                            <div className="d-inline-block px-4 py-1 rounded-pill bg-primary text-white fw-bold shadow-sm" style={{ fontSize: '0.9rem', letterSpacing: '0.05em' }}>
                                OFFICIAL STATEMENT OF MARKS
                            </div>
                        </div>

                        {/* Student Profile Metadata Box */}
                        <div className="p-3 mb-4 rounded-3" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                            <div className="d-flex flex-row align-items-center justify-content-between gap-3">
                                <div style={{ flex: 1 }}>
                                    <div className="row g-2">
                                        <div className="col-12 col-sm-6">
                                            <span className="text-muted small text-uppercase fw-bold" style={{ fontSize: '0.75rem', letterSpacing: '0.04em' }}>Name of the Student</span>
                                            <h6 className="fw-bold mb-0 text-dark">{profileDetails.applicantName || loggedUser.name || 'STUDENT NAME'}</h6>
                                        </div>
                                        <div className="col-12 col-sm-6">
                                            <span className="text-muted small text-uppercase fw-bold" style={{ fontSize: '0.75rem', letterSpacing: '0.04em' }}>Registration Number</span>
                                            <h6 className="fw-bold mb-0 text-primary">{profileDetails.registrationNo || loggedUser.nickname || '23BTH1001'}</h6>
                                        </div>
                                        <div className="col-12 col-sm-6">
                                            <span className="text-muted small text-uppercase fw-bold" style={{ fontSize: '0.75rem', letterSpacing: '0.04em' }}>Degree Program</span>
                                            <h6 className="fw-bold mb-0 text-dark">{profileDetails.courseCategory || 'Bachelor of Theology'}</h6>
                                        </div>
                                        <div className="col-12 col-sm-6">
                                            <span className="text-muted small text-uppercase fw-bold" style={{ fontSize: '0.75rem', letterSpacing: '0.04em' }}>Degree Code</span>
                                            <h6 className="fw-bold mb-0 text-dark">{profileDetails.courseDetails?.courseCode || 'B.Th'}</h6>
                                        </div>
                                    </div>
                                </div>

                                <div className="text-center text-md-end flex-shrink-0">
                                    <img
                                        id="profile"
                                        src={studentAvatarUrl}
                                        onError={handleImageError}
                                        alt="Student"
                                        className="rounded-3 shadow-sm"
                                        style={{ width: '90px', height: '110px', objectFit: 'cover', border: '2px solid #cbd5e1', background: '#ffffff' }}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Marks Details Table */}
                        <div className="table-responsive mb-4">
                            <Table bordered hover className="m-0 text-center" style={{ fontSize: '0.9rem' }}>
                                <thead style={{ background: '#1e3a8a', color: '#ffffff' }}>
                                    <tr>
                                        <th style={{ width: '50px' }}>#</th>
                                        <th style={{ width: '120px' }}>Course Code</th>
                                        <th className="text-start">Course Title</th>
                                        <th style={{ width: '90px' }}>Credits</th>
                                        <th style={{ width: '90px' }}>Marks</th>
                                        <th style={{ width: '90px' }}>Points</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {markDetails && markDetails.length > 0 ? (
                                        markDetails.map((item, index) => (
                                            <tr key={index}>
                                                <td className="text-muted">{index + 1}</td>
                                                <td className="fw-bold text-primary">{item.courseCode}</td>
                                                <td className="text-start fw-medium">{item.courseName}</td>
                                                <td>{item.courseCredit}</td>
                                                <td className="fw-bold text-dark">{item.courseMarks}</td>
                                                <td>{item.coursePoints}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="6" className="py-3 text-muted">No marks recorded yet.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </div>

                        {/* Integral Assessment Summary Card */}
                        <div className="card p-3 mb-4 shadow-sm" style={{ background: '#f8fafc', border: '2px solid #cbd5e1' }}>
                            <h6 className="fw-bold text-uppercase text-center text-primary mb-3" style={{ letterSpacing: '0.06em' }}>
                                Integral Assessment & Final Evaluation
                            </h6>

                            <div className="row g-2 text-center">
                                <div className="col-6 col-md-3">
                                    <div className="p-2 rounded bg-white border">
                                        <span className="text-muted small text-uppercase">Total Credits</span>
                                        <h5 className="fw-bold my-1 text-dark">{totalCredits || 106}</h5>
                                    </div>
                                </div>
                                <div className="col-6 col-md-3">
                                    <div className="p-2 rounded bg-white border">
                                        <span className="text-muted small text-uppercase">Total Points</span>
                                        <h5 className="fw-bold my-1 text-primary">{totalPoints}</h5>
                                    </div>
                                </div>
                                <div className="col-6 col-md-3">
                                    <div className="p-2 rounded bg-white border">
                                        <span className="text-muted small text-uppercase">Credits Evaluated</span>
                                        <h5 className="fw-bold my-1 text-dark">{creditsEval}</h5>
                                    </div>
                                </div>
                                <div className="col-6 col-md-3">
                                    <div className="p-2 rounded bg-white border">
                                        <span className="text-muted small text-uppercase">Average Mark</span>
                                        <h5 className="fw-bold my-1 text-success">{avgMark}%</h5>
                                    </div>
                                </div>
                            </div>

                            <div className="row g-2 text-center mt-2">
                                <div className="col-12 col-md-4">
                                    <div className="p-2 rounded bg-white border">
                                        <span className="text-muted small text-uppercase">Comprehensive Exam</span>
                                        <h6 className="fw-bold my-1 text-dark">{compER}</h6>
                                    </div>
                                </div>
                                <div className="col-12 col-md-4">
                                    <div className="p-2 rounded bg-white border">
                                        <span className="text-muted small text-uppercase">Final Qualification</span>
                                        <h6 className="fw-bold my-1 text-primary">{finalQual}</h6>
                                    </div>
                                </div>
                                <div className="col-12 col-md-4">
                                    <div className="p-2 rounded bg-white border" style={{ borderColor: '#f59e0b' }}>
                                        <span className="text-muted small text-uppercase">Grade Conferred</span>
                                        <h6 className="fw-bold my-1" style={{ color: '#d97706' }}>
                                            {finalGrade}
                                        </h6>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Signatures & Seal Footer */}
                        <div className="row pt-4 mt-4 text-center align-items-end" style={{ borderTop: '1px dashed #cbd5e1' }}>
                            <div className="col-4">
                                <div style={{ borderBottom: '1px solid #94a3b8', width: '80%', margin: '0 auto 6px' }}></div>
                                <span className="small text-muted fw-bold">Dean of Studies</span>
                            </div>
                            <div className="col-4">
                                <div className="d-inline-block px-3 py-1 rounded border text-muted small">
                                    <i className="fa fa-shield text-primary me-1"></i> Official Seal
                                </div>
                            </div>
                            <div className="col-4">
                                <div style={{ borderBottom: '1px solid #94a3b8', width: '80%', margin: '0 auto 6px' }}></div>
                                <span className="small text-muted fw-bold">Registrar / President</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
